/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { getNetworkState, logWarn, onLine, type LogTarget } from '@amazon/vinyl-util'

/**
 * Resolves with the first successful fetch. A failure while offline is a warning and the fetch
 * runs again when the network returns, so the promise stays pending until then; a failure while
 * online rejects.
 */
export async function fetchWhenOnLine<T>(
    target: LogTarget,
    fetch: () => T | Promise<T>
): Promise<T> {
    const network = getNetworkState()
    for (;;) {
        try {
            return await fetch()
        } catch (error) {
            if (onLine()) throw error
            logWarn(target, 'waiting for the network', error)
            await new Promise<void>((resolve) =>
                network.on('online', () => resolve(), { once: true })
            )
        }
    }
}
